import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type Theme = 'light' | 'dark' | 'system';

export interface ThemeState {
  theme: Theme;
  /** Resolved theme actually applied to the document (never 'system'). */
  resolved: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  /** Re-evaluate the resolved theme. Call when the OS preference changes. */
  applyTheme: () => void;
}

export const THEME_STORAGE_KEY = 'tgc.theme';

function prefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolve(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') return prefersDark() ? 'dark' : 'light';
  return theme;
}

function applyToDocument(resolved: 'light' | 'dark') {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (resolved === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
  root.dataset.theme = resolved;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'system',
      resolved: 'light',
      setTheme: (theme) => {
        const resolved = resolve(theme);
        applyToDocument(resolved);
        set({ theme, resolved });
      },
      applyTheme: () => {
        const resolved = resolve(get().theme);
        applyToDocument(resolved);
        set({ resolved });
      },
    }),
    {
      name: THEME_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ theme: state.theme }),
      version: 1,
      onRehydrateStorage: () => (state) => {
        if (state) state.applyTheme();
      },
    },
  ),
);

/**
 * Bootstrap the theme on app start. Call once from `main.tsx` before render so
 * the correct class is set on `<html>` and we avoid a flash of incorrect
 * theme.
 */
export function bootstrapTheme(): void {
  const store = useThemeStore.getState();
  store.applyTheme();

  if (typeof window !== 'undefined' && window.matchMedia) {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = () => {
      if (useThemeStore.getState().theme === 'system') {
        useThemeStore.getState().applyTheme();
      }
    };
    // Use modern listener if available; fall back to addListener for older browsers.
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', listener);
    } else if (typeof media.addListener === 'function') {
      media.addListener(listener);
    }
  }
}
